import { useEffect, useRef } from 'react';
import type { AudioManager } from '../../audio/manager.js';

type Star = {
  x: number;
  y: number;
  radius: number;
  vx: number;
  vy: number;
  phase: number;
  twinkle: number;
  yellow: boolean;
  // Pointer-repulsion nudge (canvas-space pixels, decays per frame)
  nudgeX: number;
  nudgeY: number;
};

const NEON_BLUE: readonly [number, number, number] = [74, 88, 255];
const VIVID_YELLOW: readonly [number, number, number] = [255, 212, 0];
const STAR_WHITE: readonly [number, number, number] = [238, 244, 255];
const RED_ORANGE: readonly [number, number, number] = [255, 98, 70];

const SEGMENTS = 64;
const STAR_COUNT = 46;
const TAU = Math.PI * 2;

function mix(a: readonly [number, number, number], b: readonly [number, number, number], t: number): string {
  const k = Math.max(0, Math.min(1, t));
  const r = Math.round(a[0] + (b[0] - a[0]) * k);
  const g = Math.round(a[1] + (b[1] - a[1]) * k);
  const bl = Math.round(a[2] + (b[2] - a[2]) * k);
  return `rgb(${r},${g},${bl})`;
}

type Stop = { at: number; color: readonly [number, number, number] };
const COLOR_STOPS: readonly Stop[] = [
  { at: 0, color: [52, 70, 255] },
  { at: 0.3, color: NEON_BLUE },
  { at: 0.48, color: STAR_WHITE },
  { at: 0.58, color: VIVID_YELLOW },
  { at: 0.76, color: RED_ORANGE },
];

function ramp(value: number): string {
  const v = Math.max(0, Math.min(1, value));
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const a = COLOR_STOPS[i];
    const b = COLOR_STOPS[i + 1];
    if (v >= a.at && v <= b.at) {
      const span = b.at - a.at;
      return mix(a.color, b.color, span === 0 ? 1 : (v - a.at) / span);
    }
  }
  return mix(COLOR_STOPS[0].color, COLOR_STOPS.at(-1)!.color, v);
}

function makeStars(reduced: boolean): Star[] {
  const count = reduced ? 18 : STAR_COUNT;
  return Array.from({ length: count }, () => ({
    x: Math.random(),
    y: Math.random(),
    radius: 0.6 + Math.random() * 1.6,
    vx: (Math.random() - 0.5) * 0.02,
    vy: (Math.random() - 0.5) * 0.02,
    phase: Math.random() * TAU,
    twinkle: 0.8 + Math.random() * 2.2,
    yellow: Math.random() < 0.22,
    nudgeX: 0,
    nudgeY: 0,
  }));
}

export function MenuVisualizer({ audio }: { audio: AudioManager }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let stars = makeStars(reduced);
    let rafId = 0;
    let el = performance.now();
    let bus = 0;

    // Pointer position in canvas-logical px (updated passively)
    let pointerCanvasX = -9999;
    let pointerCanvasY = -9999;
    const STAR_REPULSE_RADIUS = 90;  // logical px
    const STAR_REPULSE_STRENGTH = 22; // max px kick at contact
    const NUDGE_DECAY = 0.82; // per-frame decay factor (higher = slower settle)

    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      // Canvas is positioned with inset: -30%, so we offset accordingly
      pointerCanvasX = (e.clientX - rect.left) * (canvas.width / dpr / rect.width);
      pointerCanvasY = (e.clientY - rect.top) * (canvas.height / dpr / rect.height);
    };
    const onPointerLeave = () => { pointerCanvasX = -9999; pointerCanvasY = -9999; };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave, { passive: true });

    // Historial de energía por segmento para interpolar y crear el efecto wave fluido
    const smoothedEnergy = new Float32Array(SEGMENTS).fill(0.2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const size = Math.max(48, Math.min(rect.width, rect.height || rect.width));
      canvas.width = Math.round(size * dpr);
      canvas.height = Math.round(size * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const growStars = () => {
      if (stars.length >= STAR_COUNT) return;
      const needed = STAR_COUNT - stars.length;
      stars = [...stars, ...makeStars(false).slice(0, needed)];
    };

    const frame = (now: number) => {
      const dt = Math.min(64, now - el);
      el = now;
      const t = now * 0.001;
      
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      const centerX = width / 2;
      const centerY = height / 2;
      const baseR = width * 0.2375;

      let frequencies: Uint8Array<ArrayBuffer> | null = null;
      let bass = 0;
      const analyser = audio.getMenuAnalyser();
      
      if (analyser) {
        frequencies = new Uint8Array(analyser.frequencyBinCount) as Uint8Array<ArrayBuffer>;
        analyser.getByteFrequencyData(frequencies);
        
        const bassBins = Math.max(4, Math.floor(frequencies.length * 0.35));
        for (let i = 0; i < bassBins; i++) {
          bass += frequencies[i];
        }
        bass = bass / bassBins / 255;
      }
      
      bus += (bass - bus) * 0.2;
      ctx.clearRect(0, 0, width, height);

      // Halo reactivo de graves
      const haloRadius = baseR * (1.4 + bus * 0.6);
      const halo = ctx.createRadialGradient(centerX, centerY, baseR * 0.4, centerX, centerY, haloRadius);
      halo.addColorStop(0, `rgba(74,88,255,${0.15 + bus * 0.25})`);
      halo.addColorStop(0.6, `rgba(74,88,255,${0.05 + bus * 0.1})`);
      halo.addColorStop(1, 'rgba(74,88,255,0)');
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, width, height);

      const amp = baseR * 0.22;
      const step = TAU / SEGMENTS;
      // Rotación suave general del anillo para que la onda viaje de forma continua
      const rotation = reduced ? 0 : t * 0.35; 
      const wavePhase = reduced ? 0 : t * 2.0;

      // Actualizamos y suavizamos la energía de cada segmento (efecto de inercia/onda)
      for (let s = 0; s < SEGMENTS; s++) {
        let targetEnergy = 0.22;
        if (frequencies && frequencies.length > 0) {
          // Mapeo envolvente usando seno para repartir armónicamente los datos por todo el círculo
          const sampleIndex = Math.floor(Math.abs(Math.sin(s * 0.1 + t)) * (frequencies.length * 0.5));
          const val = frequencies[sampleIndex] / 255;
          targetEnergy = 0.2 + val * 0.8;
        } else {
          // Onda de respaldo armónica si no hay música reproduciéndose activamente
          targetEnergy = 0.25 + Math.sin(s * 0.3 + t * 3) * 0.15;
        }
        // Interpola suavemente el valor actual hacia el nuevo objetivo (crea el efecto wave fluido)
        smoothedEnergy[s] += (targetEnergy - smoothedEnergy[s]) * 0.25;
      }

      ctx.save();
      ctx.shadowBlur = 15;
      ctx.shadowColor = 'rgba(74,88,255,0.6)';

      for (let s = 0; s < SEGMENTS; s++) {
        const a0 = s * step + rotation;
        const a1 = (s + 1) * step + rotation;
        const energy = smoothedEnergy[s];

        const r0 = baseR * 0.96;
        const r1 = baseR * 0.96 + amp * energy;
        const wave = 0.5 + 0.28 * Math.sin(a0 * 3 + wavePhase);
        const heat = Math.min(1, energy * 0.78 + wave * 0.22);

        ctx.fillStyle = ramp(heat);
        ctx.beginPath();
        ctx.moveTo(centerX + Math.cos(a0) * r0, centerY + Math.sin(a0) * r0);
        ctx.lineTo(centerX + Math.cos(a0) * r1, centerY + Math.sin(a0) * r1);
        ctx.arc(centerX, centerY, r1, a0, a1);
        ctx.lineTo(centerX + Math.cos(a1) * r0, centerY + Math.sin(a1) * r0);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();

      // Anillo base interior
      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseR * 0.96, 0, TAU);
      ctx.strokeStyle = 'rgba(140,148,255,0.25)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      // Partículas de fondo
      if (!reduced) {
        const motion = 0.0003 + bus * 0.001;
        for (const star of stars) {
          star.x = (star.x + star.vx * motion * dt + 1) % 1;
          star.y = (star.y + star.vy * motion * dt + 1) % 1;
          const sx = star.x * width;
          const sy = star.y * height;

          // Pointer repulsion — apply kick then decay
          const dx = sx - pointerCanvasX;
          const dy = sy - pointerCanvasY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < STAR_REPULSE_RADIUS && dist > 0) {
            const force = (1 - dist / STAR_REPULSE_RADIUS) * STAR_REPULSE_STRENGTH;
            star.nudgeX += (dx / dist) * force;
            star.nudgeY += (dy / dist) * force;
          }
          // Smooth exponential decay toward zero
          star.nudgeX *= NUDGE_DECAY;
          star.nudgeY *= NUDGE_DECAY;

          const spark = 0.5 + 0.5 * Math.sin(t * star.twinkle + star.phase);
          const alpha = 0.15 + spark * 0.35 + bus * 0.3;
          ctx.beginPath();
          ctx.arc(sx + star.nudgeX, sy + star.nudgeY, star.radius * (0.8 + spark * 0.4), 0, TAU);
          ctx.fillStyle = star.yellow
            ? `rgba(255,212,0,${Math.min(1, alpha * 0.8)})`
            : `rgba(238,244,255,${Math.min(1, alpha)})`;
          ctx.fill();
        }
        if (dt > 0) growStars();
      }

      rafId = window.requestAnimationFrame(frame);
    };

    rafId = window.requestAnimationFrame(frame);
    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [audio]);

  return <canvas ref={canvasRef} className="menu-visualizer-canvas" aria-hidden="true" />;
}